import React, { useState, useRef } from 'react';
import './VideoPlayer.css';

const VideoPlayer = ({ src, poster, type = 'video/mp4' }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current) {
      const progressBar = e.currentTarget;
      const clickX = e.nativeEvent.offsetX;
      const width = progressBar.offsetWidth;
      const newTime = (clickX / width) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="video-wrapper-container">
      <div className="video-wrapper">
        {!isPlaying && (
          <div className="placeholder-overlay">
            <img
              src={poster}
              alt="Video thumbnail"
              className="placeholder-image"
            />
            <div className="play-icon" onClick={handlePlay}>
              <svg viewBox="0 0 24 24" className="play-svg">
                <path d="M8 5v14l11-7z" fill="currentColor"/>
              </svg>
            </div>
          </div>
        )}
        
        <video
          ref={videoRef}
          className="responsive-video"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          playsInline
          preload="metadata"
          style={{ display: isPlaying ? 'block' : 'none' }}
        >
          <source src={src} type={type} />
          Your browser does not support the video tag.
        </video>
      </div>

      <div className="video-controls">
        <div className="control-buttons">
          <button 
            className="control-btn play-pause-btn" 
            onClick={isPlaying ? handlePause : handlePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" className="control-icon">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="control-icon">
                <path d="M8 5v14l11-7z" fill="currentColor"/>
              </svg>
            )}
          </button>
          
          <button 
            className="control-btn stop-btn" 
            onClick={handleStop}
            aria-label="Stop"
          >
            <svg viewBox="0 0 24 24" className="control-icon">
              <path d="M6 6h12v12H6z" fill="currentColor"/>
            </svg>
          </button>
        </div>

        <div className="progress-container">
          <span className="time-display">{formatTime(currentTime)}</span>
          
          <div className="progress-bar" onClick={handleSeek}>
            <div className="progress-track">
              <div 
                className="progress-fill" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
          
          <span className="time-display">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;